import { defineStore } from 'pinia';
import dictionaryService from './../api';
import type { IDictionary, TNames, TDictionaries, TLetter } from './../types';
import mapperSelect from '@/shared/utils/mapper-select';
import valueFragile from '@/shared/utils/value-fragile';

type DictionaryId = string;

interface State {
	dictionaries: Map<IDictionary['id'], IDictionary>;
	slugs: Map<IDictionary['slug'], DictionaryId>;
	slugsLetter: Map<TLetter['slug'], TLetter['id']>;
	names: TNames;
	dictionariesList: TDictionaries;
	selectDictionaryId: IDictionary['id'] | null;
	currentDictionary: IDictionary | null;
}

export const useDictionariesStore = defineStore('Dictionaries', {
	state: (): State => ({
		dictionaries: new Map(),
		slugs: new Map(),
		slugsLetter: new Map(),
		names: [],
		dictionariesList: [],
		selectDictionaryId: null,
		currentDictionary: null,
	}),

	getters: {
		selectNames: ({ names }) => mapperSelect(names, ['id', 'name_short']),

		dictionaryIdBySlug: ({ slugs }) => (slug: IDictionary['slug']) => slugs.get(slug) ?? '-1',

		dictionaryById: ({ dictionaries }) => (id: number) => dictionaries.get(id),

		dictionaryLetterIdBySlug: ({ slugsLetter }) => (slug: TLetter['slug']) => valueFragile(
			slugsLetter.get(slug),
			'dictionaries, dictionaryLetterIdBySlug',
			'-1',
		),

		dictionaryDataBySlug: ({ dictionariesList }) => (slug: IDictionary['slug']) => dictionariesList.find((dict) => dict.slug === slug),
	},

	actions: {
		resetSelectDictionaryId() {
			this.selectDictionaryId = null;
		},

		async getDictionariesNames() {
			if (this.names.length > 0) return this.names;

			const data = await dictionaryService.fetchDictionariesNames();

			if ('error' in data) {
				console.error(data);
				return [];
			}

			this.names = data;

			return data;
		},

		async getDictionaryById(id: number) {
			if (this.dictionaries.has(id)) {
				return this.dictionaries.get(id) ?? null;
			}

			const data = await dictionaryService.fetchDictionaryById(id);

			if ('error' in data) {
				console.error(data);
				return null;
			}

			this.dictionaries.set(id, data);

			/** Фиксируем slug код энциклопедии в словарь */
			this.slugs.set(data.slug, String(id));

			/** Фиксируем slug коды каждой буквы в словарь */
			if (data.letters?.length > 0) {
				// TODO: проверка на существование
				data.letters.forEach((letter) => {
					this.slugsLetter.set(letter.slug, letter.id);
				});
			}

			return data;
		},

		async getDictionaries() {
			if (this.dictionariesList.length > 0) return this.dictionariesList;

			const data = await dictionaryService.fetchDictionaries();

			if ('error' in data) {
				console.error(data);
				return [];
			}

			this.dictionariesList = data;

			return data;
		},
	},
});
